/**
 * TicketComments: the people's thread on one ticket, the rail's second tab
 * beside the @yagni conversation. It is backed by `useWorkItemComments`, the
 * same thread the case file reads plan answers and next-step notes from; this
 * only wraps its `send`.
 */
import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { useWorkItemComments } from "../hooks/useWorkItemComments.js";

function relTime(iso: string): string {
  const diff = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (diff < 1) return "now";
  if (diff < 60) return `${diff}m`;
  const h = Math.round(diff / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function TicketComments({ workItemId }: { workItemId: string }) {
  const { comments, loading, error, send } = useWorkItemComments(workItemId);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await send(body);
      setDraft("");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="tri-chat-scroll wk-comments" data-testid="work-comments">
        {loading ? (
          <div className="tri-chat-empty">
            <p className="tri-chat-empty-d">Loading comments…</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="tri-chat-empty">
            <div className="wk-cm-empty-ic" aria-hidden>
              <MessageSquare width={26} height={26} />
            </div>
            <p className="tri-chat-empty-t">No comments yet.</p>
            <p className="tri-chat-empty-d">
              For your team to deliberate. Distinct from the @yagni thread.
            </p>
          </div>
        ) : (
          comments.map((c) => {
            const isAgent = c.authorType === "agent";
            const name = c.authorName ?? (isAgent ? "@yagni" : "Teammate");
            const initials = name
              .replace(/^@/, "")
              .split(/\s+/)
              .map((w) => w[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <div key={c.id} className={"wk-cm" + (isAgent ? " yagni" : "")}>
                <div className="wk-cm-av" aria-hidden>
                  <span className="wk-cm-initials">{initials || "?"}</span>
                </div>
                <div className="wk-cm-body">
                  <div className="wk-cm-head">
                    <span className="wk-cm-nm">{name}</span>
                    <span className="wk-cm-time">{relTime(c.createdAt)}</span>
                  </div>
                  <p className="wk-cm-text">{c.body}</p>
                </div>
              </div>
            );
          })
        )}
        {error && (
          <p className="wk-gate-err" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="tri-chat-foot">
        <div className="wk-cm-composer">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            rows={2}
            placeholder="Comment… for your team"
            aria-label="Add a comment"
          />
          <button
            type="button"
            className="wk-cm-send"
            onClick={() => void submit()}
            disabled={!draft.trim() || sending}
            aria-label="Post comment"
          >
            <Send width={15} height={15} aria-hidden />
          </button>
        </div>
      </div>
    </>
  );
}
